import Page from "@/components/layout/Page";
import { ParticleBackground } from "@/components/ParticleBackground";
import { FileText } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { SEOHead } from "@/components/seo/SEOHead";
import { ArticleSchema, BreadcrumbSchema } from "@/components/seo/StructuredData";
import chrisPhoto from "@/assets/team/chris.png";
import georgiosPhoto from "@/assets/team/georgios.png";
import davePhoto from "@/assets/team/dave.png";
import andrewPhoto from "@/assets/team/andrew.png";
import nataliePhoto from "@/assets/team/natalie.png";

export default function ResearchStudies() {
  return (
    <>
      <SEOHead
        title="Research & Studies | Evidence-Based Sensory Tools"
        description="Independent MBA research from University of Bristol (2021) examining Samboards' market potential, evidence base, and effectiveness for ADHD, autism, and sensory support."
        canonical="/research-studies"
        keywords="sensory tools research, evidence-based fidget tools, ADHD research, autism sensory study, tactile learning research"
      />
      <ArticleSchema
        headline="MBA Market Research Report – University of Bristol (2021)"
        description="Independent research conducted by MBA students examining Samboards' market potential, evidence base, and strategic recommendations for growth."
        datePublished="2021-01-01"
        dateModified="2021-12-31"
        author="MBA Analytics and Solutions Team, University of Bristol"
        keywords={["sensory tools", "market research", "ADHD", "autism", "tactile learning", "evidence-based tools"]}
      />
      <BreadcrumbSchema items={[
        { name: "Home", url: "/" },
        { name: "Feedback", url: "/feedback" },
        { name: "Research & Studies", url: "/research-studies" }
      ]} />
      
      <div className="min-h-screen overflow-hidden bg-gradient-to-b from-[#020817] via-[#04151F] to-[#020817] text-slate-50">
        <ParticleBackground />

        <Page>
        <section className="pb-24 space-y-16">
          {/* Header */}
          <header className="mb-10 md:mb-14 max-w-4xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500/10">
                <FileText className="h-6 w-6 text-emerald-400" />
              </div>
              <p className="text-xs font-semibold tracking-[0.2em] text-emerald-300 uppercase">Research & Evidence</p>
            </div>
            <h1 className="text-3xl md:text-[40px] leading-tight font-semibold page-title-gradient mb-4">
              MBA Market Research Report – University of Bristol (2021)
            </h1>
            <p className="text-lg text-slate-300/85 leading-relaxed">
              Independent research conducted by MBA students examining Samboards' market potential, evidence base, and strategic recommendations for growth.
            </p>
          </header>

          {/* Introduction Section */}
          <div className="space-y-6">
            <h2 className="text-2xl md:text-3xl font-semibold text-emerald-300 tracking-wide">Introduction</h2>
            <div className="prose prose-invert max-w-none">
              <p className="text-lg leading-relaxed text-slate-200">
                This project proposal was developed by the MBA Analytics and Solutions team from the University of Bristol in 2021. The team conducted comprehensive market research and analysis to support Samboards™ Ltd in identifying optimal routes to market and expanding their evidence base for tactile sensory products.
              </p>
            </div>
          </div>

          <Separator className="bg-slate-700/50" />

          {/* The Team Section */}
          <div className="space-y-6">
            <h2 className="text-2xl md:text-3xl font-semibold text-emerald-300 tracking-wide">Meet the Team</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 md:gap-8">
              {[
                { name: "Chris Rooum", photo: chrisPhoto },
                { name: "Georgios Chatzakis", photo: georgiosPhoto },
                { name: "Dave Pike", photo: davePhoto },
                { name: "Andrew Knowles", photo: andrewPhoto },
                { name: "Natalie Bynorth", photo: nataliePhoto }
              ].map((member) => (
                <div key={member.name} className="flex flex-col items-center space-y-3 group">
                  <div className="relative w-24 h-24 md:w-32 md:h-32 rounded-full overflow-hidden border-2 border-emerald-400/20 shadow-[0_0_20px_rgba(16,185,129,0.15)] transition-all duration-300 group-hover:border-emerald-400/40 group-hover:shadow-[0_0_30px_rgba(16,185,129,0.3)] group-hover:scale-105">
                    <img 
                      src={member.photo} 
                      alt={`${member.name} - MBA Research Team Member`}
                      className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-300"
                    />
                  </div>
                  <div className="text-center">
                    <p className="text-slate-200 font-medium text-sm md:text-base">{member.name}</p>
                    <p className="text-xs md:text-sm text-slate-400">MBA Student</p>
                    <p className="text-xs text-slate-500">University of Bristol</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <Separator className="bg-slate-700/50" />

          {/* Client Requirements Section */}
          <div className="space-y-6">
            <h2 className="text-2xl md:text-3xl font-semibold text-emerald-300 tracking-wide">Client Requirements</h2>
            <div className="prose prose-invert max-w-none">
              <p className="text-lg leading-relaxed text-slate-200 mb-4">
                Following our first meeting with Samboards™ Ltd representatives (Client) we highlighted the following problems:
              </p>
              <ul className="space-y-3 text-slate-200">
                <li>A difficulty with the route to market</li>
                <li>A disparate approach to the potential markets of the Samboards product</li>
                <li>Limited social media and networking activity</li>
                <li>No substantial Literature search or analysis performed for evidence on the areas the client has proposed as potential markets: Schizophrenia, Self-harm & Mental Health, Stroke Rehab, Autism, Stress and Anxiety, Neurological disorders</li>
                <li>Case studies of Samboards™ products not freely available to customers</li>
                <li>Adequate links to trial the product had not been yet been fully developed</li>
                <li>Potential possibilities with the design, size and colour of the product</li>
                <li>Potential opportunities with joint ventures and other funding pathways</li>
              </ul>
            </div>
          </div>

          <Separator className="bg-slate-700/50" />

          {/* Our Approach Section */}
          <div className="space-y-6">
            <h2 className="text-2xl md:text-3xl font-semibold text-emerald-300 tracking-wide">Our Approach</h2>
            <div className="prose prose-invert max-w-none space-y-6">
              <p className="text-lg leading-relaxed text-slate-200">
                From the areas highlighted the team have isolated one clear area to be concentrated on within the project.
              </p>
              
              <div className="rounded-xl border border-emerald-400/20 bg-emerald-900/10 p-6 backdrop-blur-md">
                <h3 className="text-xl font-semibold text-emerald-300 mb-4">Objective</h3>
                <p className="text-slate-200 mb-4">
                  To provide support and advice in route to market for Samboards™:
                </p>
                <ul className="space-y-2 text-slate-200">
                  <li>The team will do this by researching markets through various sources to highlight these markets via market research by highlighting any barriers that may need to be overcome before access can be obtained.</li>
                  <li>To expand its evidence base through wider and deeper literature research.</li>
                  <li>To gain focus of the specific market to be targeted.</li>
                </ul>
              </div>

              <h3 className="text-xl font-semibold text-slate-100 mt-8">Marketing</h3>
              <p className="text-slate-200">
                Samboards current approach to marketing is as follows:
              </p>
              <ul className="space-y-3 text-slate-200">
                <li><strong className="text-emerald-300">Website</strong> – opportunity to make this more interactive. Why not add a forum for users to discuss where they have found benefits and use this to market product.</li>
                <li><strong className="text-emerald-300">Dead links</strong> – on the website there is a dead link to a video explaining how to use. We have made our own which we have shared with participants (show video).</li>
                <li><strong className="text-emerald-300">Social Media</strong> – Positive user experiences good, but need to be more frequent.</li>
                <li><strong className="text-emerald-300">Twitter</strong> – not really used, could be really good for engagement if you can get into conversations with the right people. Searches can help here.</li>
                <li><strong className="text-emerald-300">Press</strong> – story on Rock the Cotswolds is really positive, there must be other publications and websites that are looking for similar copy.</li>
                <li><strong className="text-emerald-300">SEO</strong> - could be improved with first page of Google links to webpage and social media when searching for Samboards – however when searching for sensory tools or sensory boards there are no mentions of Samboards within the first 5 pages.</li>
              </ul>
            </div>
          </div>

          <Separator className="bg-slate-700/50" />

          {/* Evidence Base Section */}
          <div className="space-y-6">
            <h2 className="text-2xl md:text-3xl font-semibold text-emerald-300 tracking-wide">Evidence Base</h2>
            <div className="prose prose-invert max-w-none space-y-6">
              <div className="rounded-xl border border-slate-700/60 bg-slate-900/70 p-6 backdrop-blur-md">
                <h3 className="text-lg font-semibold text-emerald-300 mb-3">Brain Activity Research</h3>
                <p className="text-slate-200">
                  A group looked into the real time brain activity through FMRI compared novel tasks to repetitive tasks found that while both decreased mind wandering relative to no task the novel task was significantly better. Fidgeting increases concentration.
                </p>
              </div>

              <div className="rounded-xl border border-slate-700/60 bg-slate-900/70 p-6 backdrop-blur-md">
                <h3 className="text-lg font-semibold text-emerald-300 mb-3">Stress balls to focus the attention of 11-12 year olds</h3>
                <p className="text-slate-200">
                  The frequency of distraction incidents decreased during both direct instruction and independent practice when students used stress balls. Kinesthetic learners used the stress balls more consistently and their attention spans increased more when compared to other learners.
                </p>
              </div>

              <div className="rounded-xl border border-slate-700/60 bg-slate-900/70 p-6 backdrop-blur-md">
                <h3 className="text-lg font-semibold text-emerald-300 mb-3">Whole body fidgeting is effective for attention in ADHD</h3>
                <p className="text-slate-200">
                  Katherine Lsbister – Leading the research into this area
                </p>
              </div>

              <div className="rounded-xl border border-slate-700/60 bg-slate-900/70 p-6 backdrop-blur-md">
                <h3 className="text-lg font-semibold text-emerald-300 mb-3">Harm minimisation in self-harm</h3>
                <p className="text-slate-200">
                  There is currently only anecdotal evidence of the effectiveness of this form of treatment.
                </p>
              </div>

              <div className="rounded-xl border border-emerald-400/20 bg-emerald-900/10 p-6 backdrop-blur-md">
                <h3 className="text-xl font-semibold text-emerald-300 mb-4">Sensory Modulation Theory</h3>
                <h4 className="text-lg font-medium text-slate-100 mb-3">Arousal state (Kimball 1993)</h4>
                <p className="text-slate-200 leading-relaxed">
                  Sensory modulation problems are changes in arousal state due to the influences of sensation. People who have sensory system modulation problems have more changeable arousal or reaction levels than normal. This results in problems with adaptive responses because their systems lacks the stability to stay within a healthy range. These behavioural phenomenon can be related to the inverted U shaped curve showing the importance of maintaining sensory system modulation within a range of normal. In this conceptualisation, performance and adaptive capacity are poor at both low and high levels of arousal. In the middle ranges of arousal, performance and adaptation are optimal. As single sensory systems do not function independently arousal in several systems can combine. It is this combining of arousal systems which allows modulation of the sensory system through tactile stimulation.
                </p>
              </div>
            </div>
          </div>

          <Separator className="bg-slate-700/50" />

          {/* Home Schooling Study Section */}
          <div className="space-y-6">
            <h2 className="text-2xl md:text-3xl font-semibold text-emerald-300 tracking-wide">Home Schooling Study</h2>
            <div className="prose prose-invert max-w-none space-y-6">
              <h3 className="text-xl font-semibold text-slate-100">Market research</h3>
              <p className="text-slate-200">
                Was the home schooling market a viable one to aim at during this time of rapid growth?
              </p>
              <ul className="space-y-2 text-slate-200">
                <li>50 families were recruited to trial the product for a week while home schooling</li>
                <li>After trialling the product each family was asked to fill out an online questionnaire</li>
                <li>Each family was sent all three Samboards along with a welcome pack</li>
              </ul>

              <div className="rounded-xl border border-slate-700/60 bg-slate-900/70 p-6 backdrop-blur-md mt-6">
                <h4 className="text-lg font-semibold text-emerald-300 mb-4">Welcome to Samboards</h4>
                <p className="text-slate-200 mb-4">
                  Samboards would like you to try their innovative new sensory product and feedback to them your thoughts to assist them in bringing you the best possible sensory aid to home schooling.
                </p>
                
                <h5 className="text-md font-semibold text-slate-100 mb-2">An introduction to their new Samboards</h5>
                <p className="text-slate-200 mb-4">
                  The Samboard has been developed from over 30 years of experience in child sensory development using the best possible materials to create a sensory board designed to assist learning by helping control sensory input.
                </p>
                <p className="text-slate-200 mb-4">
                  Simply fiddle with the Samboard during your home schooling lesson to hopefully experience benefits in areas such as attention.
                </p>

                <h5 className="text-md font-semibold text-slate-100 mb-2">An explanation of what it did and how it may help</h5>
                <p className="text-slate-200 mb-4">
                  The Samboard has different shapes and textures to offer all sorts of sensory input which can be gained by playing and fiddling with the Samboard within your fingers. This sort of sensory input has been shown to help improve factors such as attention and emotional regulation in children.
                </p>

                <h5 className="text-md font-semibold text-slate-100 mb-2">Directions for use</h5>
                <p className="text-slate-200">
                  The Samboard is a tool to assist attention while performing other tasks. The board should be held within your hand using your fingers and thumb to move it around, squeeze, rub, roll, flick and generally fiddle with. Your attention should not be on the Samboard. Fiddle with the board while listening in a lesson, fiddle with the board trying to do a puzzle in your head, fiddle with the board in one hand while writing with the other.
                </p>
                <p className="text-slate-200 mt-3">
                  The sensory board is not designed to be a toy and should not be thought of as a toy; it is designed to be fiddled with while doing other things. Play with the board for a little while to become acquainted with it before starting the lesson then keep the board in your hand through the lesson playing with it in whatever way feels best.
                </p>
              </div>

              <div className="rounded-xl border border-slate-700/60 bg-slate-900/70 p-6 backdrop-blur-md">
                <h4 className="text-lg font-semibold text-emerald-300 mb-3">The Questionnaire</h4>
                <p className="text-slate-200 mb-3">
                  Created using Bristol Online - 13 Questions using multiple choice and free text
                </p>
                <p className="text-slate-200 mb-3">We captured:</p>
                <ul className="space-y-2 text-slate-200">
                  <li>Any learning disabilities</li>
                  <li>Age and Gender</li>
                  <li>First impressions</li>
                  <li>How they would hope it would help</li>
                  <li>How long their child used it</li>
                  <li>What effect it had</li>
                  <li>How much of an effect it had</li>
                  <li>Was their child distracted by the Samboard</li>
                  <li>Would they purchase it and how much for</li>
                  <li>Any recommendations</li>
                </ul>
              </div>
            </div>
          </div>

          <Separator className="bg-slate-700/50" />

          {/* Analysis Section */}
          <div className="space-y-6">
            <h2 className="text-2xl md:text-3xl font-semibold text-emerald-300 tracking-wide">Analysis</h2>
            <div className="prose prose-invert max-w-none space-y-6">
              <p className="text-lg text-slate-200">
                Of the original sample responses were received via an online survey.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="rounded-xl border border-slate-700/60 bg-slate-900/70 p-6 backdrop-blur-md text-center">
                  <p className="text-4xl font-bold text-emerald-400 mb-2">30%</p>
                  <p className="text-slate-300">Had a known diagnosis of learning or behavioural difficulties</p>
                </div>
                <div className="rounded-xl border border-slate-700/60 bg-slate-900/70 p-6 backdrop-blur-md text-center">
                  <p className="text-4xl font-bold text-emerald-400 mb-2">70%</p>
                  <p className="text-slate-300">Of participants were male</p>
                </div>
                <div className="rounded-xl border border-slate-700/60 bg-slate-900/70 p-6 backdrop-blur-md text-center">
                  <p className="text-4xl font-bold text-emerald-400 mb-2">3</p>
                  <p className="text-slate-300">Key age groups: 0-7, 7-11, and 11-16</p>
                </div>
              </div>

              <h3 className="text-xl font-semibold text-slate-100 mt-8">First Impressions</h3>
              <p className="text-slate-200 mb-4">
                First impressions of Samboards was varied:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="rounded-xl border border-slate-700/60 bg-slate-900/70 p-4 backdrop-blur-md">
                  <p className="text-slate-300 italic">"High level of sensory feedback. Not sure how it would help. It's a bit small."</p>
                </div>
                <div className="rounded-xl border border-slate-700/60 bg-slate-900/70 p-4 backdrop-blur-md">
                  <p className="text-slate-300 italic">"I like it, it feels nice. Good idea, I instantly fiddled with it myself!"</p>
                </div>
                <div className="rounded-xl border border-slate-700/60 bg-slate-900/70 p-4 backdrop-blur-md">
                  <p className="text-slate-300 italic">"Good pocket size. Son wasn't certain how they were meant to help him."</p>
                </div>
                <div className="rounded-xl border border-slate-700/60 bg-slate-900/70 p-4 backdrop-blur-md">
                  <p className="text-slate-300 italic">"Unusual but instantly started twiddling it in my own fingers!"</p>
                </div>
              </div>

              <h3 className="text-xl font-semibold text-slate-100 mt-8">Usage Intentions</h3>
              <div className="rounded-xl border border-emerald-400/20 bg-emerald-900/10 p-6 backdrop-blur-md">
                <p className="text-slate-200 mb-4">In a multi-option question on what they would use a Samboard for:</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-3xl font-bold text-emerald-400">71</p>
                    <p className="text-slate-300">Improve Concentration</p>
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-emerald-400">43</p>
                    <p className="text-slate-300">Reduce Anxiety</p>
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-emerald-400">38</p>
                    <p className="text-slate-300">Comfort</p>
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-emerald-400">25</p>
                    <p className="text-slate-300">Others</p>
                  </div>
                </div>
              </div>

              <h3 className="text-xl font-semibold text-slate-100 mt-8">Effects & Time Used</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="rounded-xl border border-slate-700/60 bg-slate-900/70 p-4 backdrop-blur-md">
                  <h4 className="text-emerald-300 font-semibold mb-3">Time Interval</h4>
                  <ul className="space-y-2 text-slate-200">
                    <li>&lt; 5 mins: <span className="text-emerald-400 font-semibold">35%</span></li>
                    <li>5-10 mins: <span className="text-emerald-400 font-semibold">30%</span></li>
                    <li>&gt; 10 mins: <span className="text-emerald-400 font-semibold">35%</span></li>
                  </ul>
                </div>
                <div className="rounded-xl border border-slate-700/60 bg-slate-900/70 p-4 backdrop-blur-md">
                  <h4 className="text-emerald-300 font-semibold mb-3">Effects Observed</h4>
                  <ul className="space-y-2 text-slate-200">
                    <li>Improved Concentration: <span className="text-emerald-400 font-semibold">40</span></li>
                    <li>Little or No Effect: <span className="text-emerald-400 font-semibold">35</span></li>
                    <li>Distracted by Samboard: <span className="text-emerald-400 font-semibold">46%</span></li>
                  </ul>
                </div>
              </div>

              <h3 className="text-xl font-semibold text-slate-100 mt-8">Design Preferences</h3>
              <div className="rounded-xl border border-slate-700/60 bg-slate-900/70 p-6 backdrop-blur-md">
                <h4 className="text-emerald-300 font-semibold mb-4">Board Design Preference</h4>
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-emerald-400">50%</p>
                    <p className="text-slate-300">Rock Library</p>
                  </div>
                  <div className="text-center">
                    <p className="text-3xl font-bold text-emerald-400">32%</p>
                    <p className="text-slate-300">Unicorn Library</p>
                  </div>
                  <div className="text-center">
                    <p className="text-3xl font-bold text-emerald-400">18%</p>
                    <p className="text-slate-300">Points Library</p>
                  </div>
                </div>
                <h4 className="text-emerald-300 font-semibold mb-3">Suggested Changes</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-slate-300">Change Colour: <span className="text-emerald-400 font-semibold">30</span></p>
                    <p className="text-slate-300">Size: <span className="text-emerald-400 font-semibold">25</span></p>
                  </div>
                  <div>
                    <p className="text-slate-300">Pattern: <span className="text-emerald-400 font-semibold">64%</span></p>
                    <p className="text-slate-300">Texture: <span className="text-emerald-400 font-semibold">36%</span></p>
                  </div>
                </div>
              </div>

              <h3 className="text-xl font-semibold text-slate-100 mt-8">Recommend and Cost</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="rounded-xl border border-emerald-400/20 bg-emerald-900/10 p-6 backdrop-blur-md text-center">
                  <p className="text-5xl font-bold text-emerald-400 mb-2">55%</p>
                  <p className="text-slate-200">Would recommend Samboard</p>
                </div>
                <div className="rounded-xl border border-slate-700/60 bg-slate-900/70 p-6 backdrop-blur-md">
                  <h4 className="text-emerald-300 font-semibold mb-3">Willing to Pay</h4>
                  <ul className="space-y-2 text-slate-200">
                    <li>£0-£3: <span className="text-emerald-400 font-semibold">0%</span></li>
                    <li>£3-5: <span className="text-emerald-400 font-semibold">13%</span></li>
                    <li>£5-7: <span className="text-emerald-400 font-semibold">87%</span></li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <Separator className="bg-slate-700/50" />

          {/* Conclusions Section */}
          <div className="space-y-6">
            <h2 className="text-2xl md:text-3xl font-semibold text-emerald-300 tracking-wide">Conclusions</h2>
            <div className="prose prose-invert max-w-none">
              <ul className="space-y-3 text-slate-200">
                <li>Social media profile</li>
                <li>No focus on search engine optimisation</li>
                <li>Media profile does not sell self</li>
                <li>Grammatical errors on web site</li>
                <li>Navigation on website not user friendly</li>
                <li>Opportunity to make this more interactive</li>
                <li>No clear market strategy</li>
                <li>Buy in from parents is essential for the produce to be fully understood (home schooling market)</li>
                <li>Evidence base available but not used to provide background support for product.</li>
                <li>Clear evidence of use for Learning or behavioural difficulties.</li>
                <li>More options and research into produce design required</li>
              </ul>
            </div>
          </div>

          <Separator className="bg-slate-700/50" />

          {/* Additional Data Section */}
          <div className="space-y-6">
            <h2 className="text-2xl md:text-3xl font-semibold text-emerald-300 tracking-wide">Additional Data</h2>
            <div className="prose prose-invert max-w-none space-y-6">
              <h3 className="text-xl font-semibold text-slate-100">Product Demonstration to the Therapy Departments of Charlton Lane Older Age Mental Health Hospital and Wotton Lawn Working Age Adult Mental Health Hospital</h3>
              
              <div className="rounded-xl border border-slate-700/60 bg-slate-900/70 p-6 backdrop-blur-md">
                <h4 className="text-lg font-semibold text-emerald-300 mb-3">Charlton Lane Feedback</h4>
                <p className="text-slate-200">
                  The Occupational Therapists and Physiotherapists were interested in the product to trial with their patient group but in its current size they could not introduce it to their severe dementia or Alzheimer's patients due to a very high risk of swallowing. They explained that their more severe patient group frequently put objects into their mouths and attempt to swallow them and the Samboard would cause a significant choking hazard. It would need to be 3 to 4 times its current size before they could consider trialling it.
                </p>
              </div>

              <div className="rounded-xl border border-slate-700/60 bg-slate-900/70 p-6 backdrop-blur-md">
                <h4 className="text-lg font-semibold text-emerald-300 mb-3">Wotton Lawn Feedback</h4>
                <p className="text-slate-200">
                  Very interested in the product and saw a good utility with their emotionally unstable personality disorder patient group. They had concerns around patients sharing them if they were left around the hospital especially if they had the ability to draw blood if used vigorously enough creating an infection control risk for them. Current Covid infection control would mean that they couldn't trial them with patients. Generally they liked the product and would be interested in trialling them with patients at some point in the future.
                </p>
              </div>
            </div>
          </div>

          <Separator className="bg-slate-700/50" />

          {/* Recommendations Section */}
          <div className="space-y-6">
            <h2 className="text-2xl md:text-3xl font-semibold text-emerald-300 tracking-wide">Recommendations</h2>
            <div className="prose prose-invert max-w-none">
              <ul className="space-y-3 text-slate-200">
                <li>Revamp of website and other social media profiles by commissioning marketing consult/expert to support, to include:</li>
                <li>Focus marketing on one specific market to optimise impact.</li>
                <li>Focus on search engine optimisation to get wider coverage.</li>
                <li>User Forum for feedback and wider research.</li>
                <li>Use evidence base to develop further research in conjunction with users.</li>
                <li>Wider engagement with home schooling market.</li>
                <li>Using information found develop clear relationships with adult mental health providers.</li>
                <li>Use the base survey as template for further research and development into product development with specific user markets.</li>
                <li>Focused market research and development linked to SMART objectives with clear outcomes.</li>
              </ul>
            </div>
          </div>
        </section>
      </Page>
      </div>
    </>
  );
}
